import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { PoweroffOutlined, StopOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Descriptions, Popconfirm, Spin, Table, Tag, Typography, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import useCan from '@shared/permissions/useCan';
import ListPageHeader from '@shared/ListPageHeader';
import { CustomAttributesTags } from '@components/shared/inventory_attribute_definitions/CustomAttributesTags';
import { useInventoryAttributeDefinitions } from '@hooks/orders/useInventoryAttributeDefinitions';
import { formatDate, formatDateTime } from '@utils/dateTime';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';

import { replenishmentService } from '@services/operator/replenishmentService';
import type {
  ReplenishmentOrder,
  ReplenishmentOrderItem,
} from '@services/operator/replenishmentService';

// 補貨單狀態色彩（對齊 ReplenishmentOrder model enum）。
const STATUS_COLORS: Record<string, string> = {
  pending: 'default',
  in_progress: 'processing',
  done: 'success',
  canceled: 'error',
  closed: 'warning',
};

// 補貨單明細狀態色彩（對齊 ReplenishmentOrderItem model enum: pending / done / skipped）。
const ITEM_STATUS_COLORS: Record<string, string> = {
  pending: 'default',
  done: 'success',
  skipped: 'warning',
};

// 後端狀態機：cancel 只允許 pending（零搬移作廢）、close 只允許 in_progress（提前結束）。
// 依當前狀態擇一顯示動作鈕，避免按下去撞 AASM::InvalidTransition。
const CANCELLABLE_STATUSES = ['pending'];
const CLOSABLE_STATUSES = ['in_progress'];

function ReplenishmentOrderShow() {
  const { id } = useParams();
  const { t } = useTranslation();
  const canWrite = useCan()('inventories:write');
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<ReplenishmentOrder | null>(null);
  const [canceling, setCanceling] = useState(false);
  const [closing, setClosing] = useState(false);

  // custom_attributes 欄位渲染本地化標籤（如 "color" → "顏色"）。
  // Hook 必須在下方 early-return 之上 — Rules of Hooks。
  const attributeDefinitions = useInventoryAttributeDefinitions('operator', order?.merchant_id);
  const attributeLabelByName = useMemo(
    () => Object.fromEntries(attributeDefinitions.map((d) => [d.name, d.label ?? d.name])),
    [attributeDefinitions],
  );

  const loadOrder = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await replenishmentService.get(id);
      if (res.success) {
        setOrder(res.data);
      } else {
        handleApiError(res, { t, fallbackMessage: t('common.error.load_failed') });
      }
    } catch (error) {
      handleNetworkError(error, { t, fallbackMessage: t('common.error.load_failed') });
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  // 取消補貨單：成功後以回傳的最新 order 取代本地 state（狀態變 canceled，取消鈕自動消失）。
  const handleCancel = async () => {
    if (!id) return;
    setCanceling(true);
    try {
      const res = await replenishmentService.cancel(id);
      if (res.success) {
        message.success(t('common.message.operation_success'));
        setOrder(res.data);
      } else {
        handleApiError(res, { t, fallbackMessage: t('common.error.operation_failed') });
      }
    } catch (error) {
      handleNetworkError(error, { t, fallbackMessage: t('common.error.operation_failed') });
    } finally {
      setCanceling(false);
    }
  };

  // 提前結束補貨單：成功後以回傳的最新 order 取代本地 state（狀態變 closed，動作鈕自動消失）。
  const handleClose = async () => {
    if (!id) return;
    setClosing(true);
    try {
      const res = await replenishmentService.close(id);
      if (res.success) {
        message.success(t('common.message.operation_success'));
        setOrder(res.data);
      } else {
        handleApiError(res, { t, fallbackMessage: t('common.error.operation_failed') });
      }
    } catch (error) {
      handleNetworkError(error, { t, fallbackMessage: t('common.error.operation_failed') });
    } finally {
      setClosing(false);
    }
  };

  const columns = [
    {
      title: t('operator.replenishment_orders.item_columns.product'),
      key: 'product',
      render: (_: unknown, r: ReplenishmentOrderItem) => (
        <Link to={`/products/${r.product_id}`}>
          <div>{r.product_name}</div>
          {r.product_sku && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {r.product_sku}
            </Typography.Text>
          )}
        </Link>
      ),
    },
    {
      title: t('models.inventory.batch'),
      dataIndex: 'batch',
      key: 'batch',
      render: (v: string | null) => v || '-',
    },
    {
      title: t('models.inventory.manufacturing_date'),
      dataIndex: 'manufacturing_date',
      key: 'manufacturing_date',
      render: (v: string | null) => (v ? formatDate(v) : '-'),
    },
    {
      title: t('models.inventory.expiration_date'),
      dataIndex: 'expiration_date',
      key: 'expiration_date',
      render: (v: string | null) => (v ? formatDate(v) : '-'),
    },
    {
      title: t('models.inventory.custom_attributes'),
      dataIndex: 'custom_attributes',
      key: 'custom_attributes',
      render: (v: Record<string, unknown> | null) => (
        <CustomAttributesTags attributes={v} attributeLabelByName={attributeLabelByName} />
      ),
    },
    {
      title: t('operator.replenishment_orders.item_columns.from_shelf'),
      dataIndex: 'from_shelf_location',
      key: 'from_shelf_location',
    },
    {
      title: t('operator.replenishment_orders.item_columns.to_shelf'),
      dataIndex: 'to_shelf_location',
      key: 'to_shelf_location',
    },
    {
      title: t('operator.replenishment_orders.item_columns.suggested_quantity'),
      dataIndex: 'suggested_quantity',
      key: 'suggested_quantity',
      align: 'right' as const,
    },
    {
      title: t('operator.replenishment_orders.item_columns.moved_quantity'),
      dataIndex: 'moved_quantity',
      key: 'moved_quantity',
      align: 'right' as const,
    },
    {
      title: t('operator.replenishment_orders.item_columns.status'),
      key: 'status',
      render: (_: unknown, r: ReplenishmentOrderItem) => (
        <Tag color={ITEM_STATUS_COLORS[r.status] || 'default'}>
          {t(`enums.replenishment_item_status.${r.status}`)}
        </Tag>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!order) {
    return (
      <Alert
        message={t('operator.replenishment_orders.detail.title')}
        description={t('common.error.not_found')}
        type="warning"
        showIcon
      />
    );
  }

  const cancellable = CANCELLABLE_STATUSES.includes(order.status);
  const closable = CLOSABLE_STATUSES.includes(order.status);
  // 提前結束確認框顯示剩餘未搬筆數（pending 明細）。
  const remainingCount = (order.items ?? []).filter((i) => i.status === 'pending').length;

  // inventories:write gate：用 useCan() + disabled 包住 Popconfirm 的直接 child，
  // 不用 <Can> 包裹（<Can> 不會把 onClick 轉發給 Popconfirm 的內層 Button）。
  let actionButton: React.ReactNode;
  if (cancellable) {
    // pending（零搬移）→ 取消整張單作廢。
    actionButton = (
      <Popconfirm
        title={t('operator.replenishment_orders.cancel_confirm')}
        okText={t('common.button.confirm')}
        cancelText={t('common.button.cancel')}
        okButtonProps={{ danger: true }}
        onConfirm={handleCancel}
        disabled={!canWrite}
      >
        <Button danger icon={<StopOutlined />} loading={canceling} disabled={!canWrite}>
          {t('common.button.cancel')}
        </Button>
      </Popconfirm>
    );
  } else if (closable) {
    // in_progress（已搬過）→ 提前結束，停在當下進度、剩餘明細標 skipped。
    actionButton = (
      <Popconfirm
        title={t('operator.replenishment_orders.close_confirm', { count: remainingCount })}
        okText={t('common.button.confirm')}
        cancelText={t('common.button.cancel')}
        okButtonProps={{ danger: true }}
        onConfirm={handleClose}
        disabled={!canWrite}
      >
        <Button danger icon={<PoweroffOutlined />} loading={closing} disabled={!canWrite}>
          {t('operator.replenishment_orders.close')}
        </Button>
      </Popconfirm>
    );
  }

  return (
    <div>
      <ListPageHeader
        items={['replenishment', 'replenishment.orders', { label: order.name }]}
        actions={actionButton}
      />

      <Card style={{ marginBottom: 16 }}>
        <Descriptions bordered column={2}>
          <Descriptions.Item label={t('operator.replenishment_orders.detail.name')}>
            {order.name}
          </Descriptions.Item>
          <Descriptions.Item label={t('operator.replenishment_orders.detail.status')}>
            <Tag color={STATUS_COLORS[order.status] || 'default'}>
              {t(`enums.replenishment_order_status.${order.status}`)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('operator.replenishment_orders.detail.merchant')}>
            {order.merchant_name || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('operator.replenishment_orders.detail.created_at')}>
            {formatDateTime(order.created_at)}
          </Descriptions.Item>
          <Descriptions.Item label={t('operator.replenishment_orders.detail.completed_at')}>
            {order.completed_at ? formatDateTime(order.completed_at) : '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title={t('operator.replenishment_orders.detail.items')}>
        <Table
          columns={columns}
          dataSource={order.items ?? []}
          rowKey="id"
          pagination={false}
        />
      </Card>
    </div>
  );
}

export default ReplenishmentOrderShow;
